import { Braces, ChevronDown, ChevronRight, Eye, EyeOff, Layers3 } from "lucide-react";
import { useMemo, useState } from "react";

import {
  getRequestVolumeInvocationSiteKey,
  MAX_GROUPED_TEMPLATES,
  OTHER_INVOCATION_SITES_KEY,
} from "./useRequestVolumeTailChartOptions";

import { EntityIcon } from "@/components/ui/icons";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { cn } from "@/lib/utils";
import { EntityType } from "@/queries";
import type {
  GetRequestVolumeTailResponse,
  RequestVolumeTailTemplateSummary,
} from "@/queries/signals";

interface InvocationSiteLegendProps {
  data: GetRequestVolumeTailResponse | undefined;
  hiddenInvocationSiteKeys?: string[];
  onToggleInvocationSite?: (key: string) => void;
  onShowAllInvocationSites?: () => void;
  onSelectInvocationSite?: (summary: RequestVolumeTailTemplateSummary) => void;
}

const InvocationSiteLegend = ({
  data,
  hiddenInvocationSiteKeys = [],
  onToggleInvocationSite,
  onShowAllInvocationSites,
  onSelectInvocationSite,
}: InvocationSiteLegendProps) => {
  const [isOtherSourcesOpen, setIsOtherSourcesOpen] = useState(false);
  const templatesWithRequests = useMemo(
    () =>
      [...(data?.template_summaries ?? [])]
        .filter(summary => summary.total_requests > 0)
        .sort((left, right) => right.total_requests - left.total_requests),
    [data?.template_summaries],
  );

  const primaryTemplateCount =
    templatesWithRequests.length > MAX_GROUPED_TEMPLATES
      ? MAX_GROUPED_TEMPLATES - 1
      : MAX_GROUPED_TEMPLATES;
  const otherSources = data?.scoped_prompt ? [] : templatesWithRequests.slice(primaryTemplateCount);
  const invocationSites = useMemo(
    () =>
      data?.scoped_prompt
        ? [
            {
              promptId: data.scoped_prompt.prompt_id,
              name: data.scoped_prompt.prompt_name,
              requestCount: data.buckets.reduce(
                (total, bucket) => total + bucket.total_requests,
                0,
              ),
            },
          ]
        : templatesWithRequests.slice(0, primaryTemplateCount).map(summary => ({
            promptId: summary.prompt_id,
            name: summary.prompt_name,
            requestCount: summary.total_requests,
          })),
    [data?.scoped_prompt, data?.buckets, primaryTemplateCount, templatesWithRequests],
  );

  if (invocationSites.length === 0) {
    return null;
  }

  const hiddenKeys = new Set(hiddenInvocationSiteKeys);
  const canToggle = !data?.scoped_prompt && Boolean(onToggleInvocationSite);
  const otherSourcesHidden = hiddenKeys.has(OTHER_INVOCATION_SITES_KEY);
  const otherSourcesRequestCount = otherSources.reduce(
    (total, summary) => total + summary.total_requests,
    0,
  );
  const chartSiteCount = invocationSites.length + (otherSources.length > 0 ? 1 : 0);
  const visibleSiteCount =
    invocationSites.filter(
      site => !hiddenKeys.has(getRequestVolumeInvocationSiteKey(site.promptId)),
    ).length + (otherSources.length > 0 && !otherSourcesHidden ? 1 : 0);

  return (
    <div className="flex min-w-0 shrink-0 items-center gap-1.5 overflow-hidden px-1 pb-1.5">
      <span className="shrink-0 text-[9px] font-medium uppercase tracking-wide text-gray-400">
        Sources
      </span>
      {invocationSites.map((site, index) => {
        const key = getRequestVolumeInvocationSiteKey(site.promptId);
        const hidden = hiddenKeys.has(key);
        const visibilityIcon = hidden ? (
          <EyeOff className="h-3 w-3 shrink-0 text-gray-400" />
        ) : (
          <Eye className="h-3 w-3 shrink-0 text-blue-500" />
        );
        const toggleTitle = `${hidden ? "Show" : "Hide"} ${site.name}`;
        return (
          <button
            key={key}
            type="button"
            className={cn(
              "flex min-w-0 max-w-[11rem] items-center gap-1 rounded-md px-1.5 py-1 text-left transition-colors",
              hidden
                ? "bg-gray-50 text-gray-400 opacity-60 hover:opacity-100"
                : "bg-blue-50/70 ring-1 ring-inset ring-blue-100",
              !canToggle && "cursor-default",
            )}
            onClick={() => onToggleInvocationSite?.(key)}
            disabled={!canToggle}
            aria-pressed={!hidden}
            title={toggleTitle}
          >
            {chartSiteCount > 1 ? (
              <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded bg-white text-[8px] font-semibold tabular-nums text-gray-500 ring-1 ring-inset ring-gray-200">
                {index + 1}
              </span>
            ) : null}
            <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
              {site.promptId === null ? (
                <Braces className="h-3 w-3 text-gray-400" />
              ) : (
                <EntityIcon type={EntityType.PROMPT} size="xs" />
              )}
            </span>
            <OverflowDisplayTooltip content={site.name} className="min-w-0 flex-1">
              <span className="block truncate text-[10px] font-medium text-gray-600">
                {site.name}
              </span>
            </OverflowDisplayTooltip>
            <span className="shrink-0 text-[9px] tabular-nums text-gray-400">
              {site.requestCount}
            </span>
            {canToggle ? visibilityIcon : null}
          </button>
        );
      })}

      {otherSources.length > 0 ? (
        <Popover open={isOtherSourcesOpen} onOpenChange={setIsOtherSourcesOpen}>
          <div
            className={cn(
              "flex min-w-0 max-w-[12rem] items-center rounded-md transition-colors",
              otherSourcesHidden
                ? "bg-gray-50 text-gray-400 opacity-60 hover:opacity-100"
                : "bg-blue-50/70 ring-1 ring-inset ring-blue-100",
            )}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center gap-1 rounded-l-md py-1 pl-1.5 pr-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30"
                title={`View ${otherSources.length} other live sources`}
              >
                <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded bg-white text-[8px] font-semibold tabular-nums text-gray-500 ring-1 ring-inset ring-gray-200">
                  {invocationSites.length + 1}
                </span>
                <Layers3 className="h-3 w-3 shrink-0 text-gray-400" />
                <span className="truncate text-[10px] font-medium text-gray-600">
                  Other sources
                </span>
                <span className="shrink-0 text-[9px] tabular-nums text-gray-400">
                  {otherSourcesRequestCount}
                </span>
                <ChevronDown className="h-3 w-3 shrink-0 text-gray-400" />
              </button>
            </PopoverTrigger>
            {canToggle ? (
              <button
                type="button"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-r-md text-gray-400 transition-colors hover:bg-blue-100/60 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30"
                onClick={() => onToggleInvocationSite?.(OTHER_INVOCATION_SITES_KEY)}
                aria-pressed={!otherSourcesHidden}
                title={`${otherSourcesHidden ? "Show" : "Hide"} other sources`}
              >
                {otherSourcesHidden ? (
                  <EyeOff className="h-3 w-3" />
                ) : (
                  <Eye className="h-3 w-3 text-blue-500" />
                )}
              </button>
            ) : null}
          </div>

          <PopoverContent align="start" sideOffset={6} className="w-72 overflow-hidden p-0">
            <div className="border-b border-gray-100 px-3 py-2.5">
              <p className="text-xs font-medium text-gray-700">Other Live Sources</p>
              <p className="mt-0.5 text-[10px] text-gray-400">
                {otherSources.length} sources · {otherSourcesRequestCount} requests grouped in the
                chart
              </p>
            </div>
            <div className="scrollbar-thin scrollbar-thumb-gray-200 max-h-64 overflow-y-auto py-1">
              {otherSources.map(summary => (
                <button
                  key={getRequestVolumeInvocationSiteKey(summary.prompt_id)}
                  type="button"
                  className="group flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-gray-50 disabled:cursor-default"
                  onClick={() => {
                    onSelectInvocationSite?.(summary);
                    setIsOtherSourcesOpen(false);
                  }}
                  disabled={!onSelectInvocationSite}
                  title={`Focus ${summary.prompt_name}`}
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-gray-50">
                    {summary.prompt_id === null ? (
                      <Braces className="h-3 w-3 text-gray-400" />
                    ) : (
                      <EntityIcon type={EntityType.PROMPT} size="xs" />
                    )}
                  </span>
                  <OverflowDisplayTooltip content={summary.prompt_name} className="min-w-0 flex-1">
                    <span className="block truncate text-[11px] text-gray-600 group-hover:text-gray-800">
                      {summary.prompt_name}
                    </span>
                  </OverflowDisplayTooltip>
                  <span className="shrink-0 text-[10px] tabular-nums text-gray-400">
                    {summary.total_requests}
                  </span>
                  {onSelectInvocationSite ? (
                    <ChevronRight className="h-3 w-3 shrink-0 text-gray-300 transition-colors group-hover:text-gray-500" />
                  ) : null}
                </button>
              ))}
            </div>
            {onSelectInvocationSite ? (
              <p className="border-t border-gray-100 px-3 py-2 text-[10px] text-gray-400">
                Select a source to focus the chart.
              </p>
            ) : null}
          </PopoverContent>
        </Popover>
      ) : null}

      {visibleSiteCount === 0 ? (
        <span className="shrink-0 text-[9px] font-medium text-gray-400">All sources hidden</span>
      ) : null}
      {hiddenInvocationSiteKeys.length > 0 && canToggle ? (
        <button
          type="button"
          className="shrink-0 rounded px-1 py-0.5 text-[9px] font-medium text-blue-600 transition-colors hover:bg-blue-50"
          onClick={onShowAllInvocationSites}
        >
          Show All
        </button>
      ) : null}
      {chartSiteCount > 1 ? (
        <span className="ml-auto hidden shrink-0 text-[9px] text-gray-400 2xl:inline">
          left to right
        </span>
      ) : null}
    </div>
  );
};

export default InvocationSiteLegend;
